import {
  InnerBlocks,
  store as blockEditorStore,
  useBlockProps,
} from '@wordpress/block-editor';
import { cloneBlock } from '@wordpress/blocks';
import { useDispatch, useRegistry, useSelect } from '@wordpress/data';
import { useEffect, useMemo, useState } from '@wordpress/element';

import InspectorControls from './InspectorControls';
import Pin from './pin';
import { clampPinPosition } from './pin-positions';
import { getNoteAttributes, getNoteInnerBlocks } from '../_shared/pinned-note-defaults';
import { resolvePresetValue } from '../../utils/helpers';

import './style.scss';

export default function Edit({ attributes, setAttributes, clientId }) {
  const { rotate, showPin, pinColor, pinPosition } = attributes;

  // Position in the row, which picks the preset the fallback template below
  // fills an otherwise empty row with — so the note the author puts back after
  // clearing the third one is the third note's colour again.
  const noteIndex = useSelect(
    (select) => select(blockEditorStore).getBlockIndex(clientId),
    [clientId]
  );

  const registry = useRegistry();
  const { __unstableMarkNextChangeAsNotPersistent, replaceInnerBlocks } =
    useDispatch(blockEditorStore);

  // What a note added by hand should copy itself from, decided once.
  //
  // Empty means the author just added this note: every note the row seeds
  // arrives with its card already inside, and so does every note parsed from
  // saved content. Read during the first render rather than in the effect
  // below — InnerBlocks applies its template from an effect of its own, and a
  // child's effects run before its parent's, so by the time the effect fires
  // the note has a card in it and no longer looks new.
  //
  // The neighbour it copies is the note it was inserted after, or the one it
  // was inserted before when it lands first in the row. Null when the row is
  // empty, which is the only case left for the shipped defaults.
  const [seed] = useState(() => {
    const { getBlockCount, getBlockIndex, getBlockRootClientId, getBlocks } =
      registry.select(blockEditorStore);

    if (getBlockCount(clientId) > 0) {
      return null;
    }

    const index = getBlockIndex(clientId);
    const siblings = getBlocks(getBlockRootClientId(clientId));

    return { index, source: siblings[index - 1] ?? siblings[index + 1] ?? null };
  });

  // A note added by hand starts at the block's defaults — no tilt, no pin
  // colour, and none of the frame styling (radius, shadow, the top padding the
  // pin hangs in), which lives on the note's own attributes and so cannot come
  // from a template. Beside the notes already in the row it read as a plain
  // square box. Copy the neighbour instead of the shipped defaults, so a row an
  // author has restyled keeps growing in their style rather than reverting to
  // ours; content comes along too, as the nearest thing to edit over.
  useEffect(() => {
    if (!seed) {
      return;
    }

    // Fold into the insertion's undo step: undo should remove the note, not
    // strip it back to a bare box and leave that behind. Marks only the next
    // change, hence once per dispatch.
    __unstableMarkNextChangeAsNotPersistent();

    if (!seed.source) {
      setAttributes(getNoteAttributes(seed.index));
      return;
    }

    // `anchor` is an element id. Copying it would put two of the same id on the
    // page, which breaks every link and every stylesheet rule aimed at it.
    const { anchor, ...attributesToCopy } = seed.source.attributes;

    setAttributes(attributesToCopy);

    __unstableMarkNextChangeAsNotPersistent();
    replaceInnerBlocks(
      clientId,
      seed.source.innerBlocks.map((block) => cloneBlock(block))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Only the fallback path needs a template; a note copying a neighbour gets
  // its content from the clone above, and letting the template run first would
  // build a card just to throw it away.
  //
  // Rebuilt only when the note moves: the editor re-syncs inner blocks when the
  // template's identity changes, so this must not be a fresh array per render.
  const template = useMemo(
    () => (seed?.source ? undefined : getNoteInnerBlocks(noteIndex)),
    [seed, noteIndex]
  );

  // Mirror exactly what render.php emits, so the canvas is an honest preview.
  // The stagger is absent by design — the parent applies it to this card from
  // the outside.
  const style = {};

  if (rotate) {
    style['--note-rotate'] = `${rotate}deg`;
  }

  // Clamp exactly as render.php does, so the canvas does not misrender on a
  // value the server would have fallen back on.
  const blockProps = useBlockProps({
    className: `brandy-pinned-note is-pin-${clampPinPosition(pinPosition)}`,
    style,
  });

  return (
    <>
      <InspectorControls
        attributes={attributes}
        setAttributes={setAttributes}
        clientId={clientId}
      />

      <div {...blockProps}>
        {showPin && <Pin color={resolvePresetValue(pinColor) || undefined} />}
        <InnerBlocks template={template} templateLock={false} />
      </div>
    </>
  );
}
